// Call diagnostics ("What happened"): who ended a call and what the network did, with provenance.
// Pure types, parsing and copy; rendering is in WhatHappened.tsx.
//
// Seam: these narrow local types mirror GET /api/v1/calls/{id}/timeline and the four Call
// termination fields while packages/contracts adds the schemas. Swap them for the contract types
// once merged. Everything from the server is re-validated here: unknown enum values become
// "unknown", malformed events are dropped (and counted), and only allowlisted media fields are read,
// so nothing else in an event (for example SDP or tokens sent by mistake) can reach the page.
import type { Call } from '@bcio-voice/contracts';
import { formatDuration } from '../../lib/format.ts';

export const TERMINATION_SIDES = ['agent', 'remote', 'system', 'unknown'] as const;
export const TERMINATION_KINDS = [
  'agent_hangup_command', 'agent_device_bye', 'agent_reject', 'agent_cancel',
  'remote_hangup', 'remote_busy', 'remote_no_answer', 'remote_rejected', 'remote_cancel',
  'carrier_failure', 'registration_failure', 'signaling_lost', 'ice_failure', 'dtls_failure',
  'media_lost_auto_cleanup', 'duration_limit', 'policy_stop', 'system_cleanup', 'unknown',
] as const;
export const CONFIDENCES = ['observed', 'inferred', 'reported', 'unknown'] as const;
export const EVENT_SOURCES = ['agent_action', 'client_report', 'controller', 'pbx', 'carrier', 'system'] as const;
export const EVENT_CATEGORIES = ['signaling', 'media', 'termination', 'registration', 'policy', 'system'] as const;

export type TerminationSide = (typeof TERMINATION_SIDES)[number];
export type TerminationKind = (typeof TERMINATION_KINDS)[number];
export type Confidence = (typeof CONFIDENCES)[number];
export type EventSource = (typeof EVENT_SOURCES)[number];
export type EventCategory = (typeof EVENT_CATEGORIES)[number];

/** The four attribution fields on a Call and on the timeline's `call`. All null until the server attributes the end. */
export interface CallTermination {
  termination_side: TerminationSide | null;
  termination_kind: TerminationKind | null;
  termination_confidence: Confidence | null;
  termination_explanation: string | null;
}

/** Bounded per-leg media observations. Only these keys are ever read from an event's `data`. */
export interface MediaData {
  packets_sent?: number;
  packets_received?: number;
  packets_lost?: number;
  jitter_ms?: number;
  rtt_ms?: number;
  bytes_sent?: number;
  bytes_received?: number;
  reconnects?: number;
  audio_level_dbfs?: number;
  ice_state?: string;
  dtls_state?: string;
  connection_state?: string;
  signaling_state?: string;
}

export interface TimelineEvent {
  id: string;
  call_id: string;
  leg_id: string | null;
  participant_id: string | null;
  sequence: number;
  at: string;
  source: EventSource;
  category: EventCategory;
  kind: string;
  actor_user_id: string | null;
  device_id: string | null;
  cause_code: string | null;
  cause_detail: string | null;
  confidence: Confidence;
  terminal: boolean;
  data: MediaData;
  /** Note events only: the note this event is about, when the server named it. */
  note_id?: string | null;
}

export interface CallTimeline {
  call: CallTermination;
  events: TimelineEvent[];
  /** Events the server sent that this version couldn't read (unknown source or category, or malformed). */
  skipped: number;
}

/** The provenance rule, shown verbatim (docs/SELF-HOSTED-SERVICE-REQUIREMENTS.md). */
export const PROVENANCE_NOTE =
  'A raw carrier/SIP cause, a server-observed fact and a client-reported diagnostic are different things; "ended normally" never proves which person pressed hang up unless the actor is known.';

/** Most events read from one response; the rest are counted as skipped. */
export const MAX_EVENTS = 2000;

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
const CODE = /^[A-Za-z0-9][A-Za-z0-9_.:/-]{0,79}$/;
const STATE = /^[a-z][a-z-]{0,23}$/;
const COUNTERS = ['packets_sent', 'packets_received', 'packets_lost', 'jitter_ms', 'rtt_ms', 'bytes_sent', 'bytes_received', 'reconnects'] as const;
const STATES = ['ice_state', 'dtls_state', 'connection_state', 'signaling_state'] as const;

function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : null;
}
function id(value: unknown): string | null {
  return typeof value === 'string' && ID.test(value) ? value : null;
}
function code(value: unknown): string | null {
  return typeof value === 'string' && CODE.test(value) ? value : null;
}
function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Defence in depth for free-text causes (the server is the redactor): drops anything that looks
 * like SDP and masks addresses, SIP URIs and long token-like strings before display.
 */
export function redactCause(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.replace(/[\u0000-\u001f\u007f]+/g, ' ').trim();
  if (!text) return null;
  if (/(^|\s)v=0(\s|$)|a=ice-pwd|a=fingerprint|\bo=-\s/i.test(text)) return '[removed: session description]';
  return clip(
    text
      .replace(/\bsips?:[^\s;,>]+/gi, '[SIP address]')
      .replace(/\b(?:\d{1,3}\.){3}\d{1,3}(?::\d+)?\b/g, '[address]')
      // IPv6: compressed ("::") or four or more colons, so clock times like 20:00:01 survive.
      .replace(/(?<![\w:])(?:[0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}(?![\w:])/gi, m => (m.includes('::') || m.split(':').length > 4 ? '[address]' : m))
      .replace(/\b(bearer|token|password|authorization|cookie)\b\s*[:=]?\s*\S+/gi, '$1 [removed]')
      .replace(/[A-Za-z0-9+/_=-]{32,}/g, '[removed]'),
    300,
  );
}

/** The four attribution fields of a call (absent until the contract lane lands them: all null). */
export function terminationOf(value: unknown): CallTermination {
  const r = isRecord(value) ? value : {};
  const explanation = typeof r.termination_explanation === 'string' ? r.termination_explanation.replace(/\s+/g, ' ').trim() : '';
  return {
    termination_side: oneOf(TERMINATION_SIDES, r.termination_side),
    termination_kind: r.termination_kind == null ? null : (oneOf(TERMINATION_KINDS, r.termination_kind) ?? 'unknown'),
    termination_confidence: r.termination_confidence == null ? null : (oneOf(CONFIDENCES, r.termination_confidence) ?? 'unknown'),
    termination_explanation: explanation ? clip(explanation, 400) : null,
  };
}

export function hasAttribution(t: CallTermination): boolean {
  return t.termination_side !== null || t.termination_kind !== null;
}

function readData(value: unknown): MediaData {
  if (!isRecord(value)) return {};
  const out: MediaData = {};
  for (const key of COUNTERS) {
    const v = value[key];
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) out[key] = v;
  }
  const level = value.audio_level_dbfs;
  if (typeof level === 'number' && Number.isFinite(level) && level <= 0 && level >= -200) out.audio_level_dbfs = level;
  for (const key of STATES) {
    const v = value[key];
    if (typeof v === 'string' && STATE.test(v)) out[key] = v;
  }
  return out;
}

function readEvent(value: unknown): TimelineEvent | null {
  if (!isRecord(value)) return null;
  const eventId = id(value.id);
  const source = oneOf(EVENT_SOURCES, value.source);
  const category = oneOf(EVENT_CATEGORIES, value.category);
  const kind = code(value.kind);
  const at = typeof value.at === 'string' && !Number.isNaN(Date.parse(value.at)) ? value.at : null;
  const sequence = typeof value.sequence === 'number' && Number.isInteger(value.sequence) && value.sequence >= 0 ? value.sequence : null;
  if (!eventId || !source || !category || !kind || !at || sequence === null) return null;
  return {
    id: eventId,
    call_id: id(value.call_id) ?? '',
    leg_id: id(value.leg_id),
    participant_id: id(value.participant_id),
    sequence,
    at,
    source,
    category,
    kind,
    actor_user_id: id(value.actor_user_id),
    device_id: id(value.device_id),
    cause_code: code(value.cause_code),
    cause_detail: redactCause(value.cause_detail),
    confidence: oneOf(CONFIDENCES, value.confidence) ?? 'unknown',
    terminal: value.terminal === true,
    data: readData(value.data),
    ...(isNoteEvent({ kind }) ? { note_id: noteIdFrom(value.cause_detail) } : {}),
  };
}

/** Reads the timeline `data` object. Null when it isn't a timeline at all. Events come back in sequence order. */
export function readTimeline(value: unknown, max = MAX_EVENTS): CallTimeline | null {
  if (!isRecord(value) || !Array.isArray(value.events)) return null;
  const events: TimelineEvent[] = [];
  let skipped = 0;
  for (const raw of value.events) {
    const event = events.length < max ? readEvent(raw) : null;
    if (event) events.push(event);
    else skipped += 1;
  }
  events.sort((a, b) => a.sequence - b.sequence || Date.parse(a.at) - Date.parse(b.at));
  return { call: terminationOf(value.call), events, skipped };
}

// ---- Who ended the call -------------------------------------------------------------------------

export type Actor = { kind: 'self' } | { kind: 'teammate'; name: string | null } | { kind: 'unknown' };
export type NameLookup = (userId: string | null | undefined) => string | null;

const AGENT_KINDS: readonly TerminationKind[] = ['agent_hangup_command', 'agent_device_bye', 'agent_reject', 'agent_cancel'];

/** Explicit end commands: the API records them as terminal `agent_action` events named `<action>_requested`. */
const EXPLICIT_END_COMMANDS: ReadonlySet<string> = new Set(['hangup_requested', 'reject_requested', 'cancel_requested', 'agent_hangup_command', 'agent_reject', 'agent_cancel']);

/**
 * The person who explicitly ended the call. Only an authenticated end command the server observed
 * (a terminal `agent_action` event with an actor) names a person. A device BYE, a client report,
 * an actor id on a device event or the attributed side alone never does: those say which device,
 * not who intended what. The command event's kind (`hangup_requested`) differs from the attributed
 * termination kind (`agent_hangup_command`), so they are matched as a family, not by equality.
 */
export function attributedActor(t: CallTermination, events: readonly TimelineEvent[], selfId: string, name: NameLookup): Actor {
  const agentEnd = t.termination_side === 'agent' || (t.termination_kind !== null && AGENT_KINDS.includes(t.termination_kind));
  if (!agentEnd || t.termination_kind === 'agent_device_bye') return { kind: 'unknown' };
  const command = events.find(e => e.terminal && e.actor_user_id && e.source === 'agent_action' && e.confidence === 'observed' && EXPLICIT_END_COMMANDS.has(e.kind));
  return actorFor(command?.actor_user_id ?? null, selfId, name);
}

/** Whose device the terminal event came from, for device wording only. Says nothing about intent. */
export function deviceOwner(events: readonly TimelineEvent[], selfId: string, name: NameLookup): Actor {
  const terminal = events.find(e => e.terminal && e.actor_user_id);
  return actorFor(terminal?.actor_user_id ?? null, selfId, name);
}

export function actorFor(userId: string | null, selfId: string, name: NameLookup): Actor {
  if (!userId) return { kind: 'unknown' };
  if (userId === selfId) return { kind: 'self' };
  return { kind: 'teammate', name: name(userId) };
}

const subject = (a: Actor) => (a.kind === 'self' ? 'You' : a.kind === 'teammate' ? a.name ?? 'A teammate' : 'Someone on your team');
const possessive = (a: Actor) => (a.kind === 'self' ? 'Your' : a.kind === 'teammate' ? (a.name ? `${a.name}’s` : 'A teammate’s') : 'A team');

/**
 * One headline for the call's end, from the four fields. `actor` is the person behind an explicit end
 * command (attributedActor); `owner` is whose device a device-originated end came from (deviceOwner).
 */
export function terminationHeadline(t: CallTermination, actor: Actor, owner: Actor = { kind: 'unknown' }): string {
  switch (t.termination_kind) {
    case 'agent_hangup_command':
    case 'agent_reject':
    case 'agent_cancel':
      return agentHeadline(actor);
    case 'agent_device_bye':
      return deviceHeadline(owner);
    case 'remote_hangup':
    case 'remote_cancel':
      return 'The other side ended the call';
    case 'remote_busy':
      return 'The other side was busy';
    case 'remote_no_answer':
      return 'The other side didn’t answer';
    case 'remote_rejected':
      return 'The call was rejected';
    case 'carrier_failure':
      return 'The call failed in the phone network';
    case 'registration_failure':
    case 'signaling_lost':
    case 'ice_failure':
    case 'dtls_failure':
      return 'The call ended because of a connection problem';
    case 'media_lost_auto_cleanup':
      return 'Ended automatically after the audio connection was lost';
    case 'duration_limit':
    case 'policy_stop':
      return 'Ended by a workspace limit';
    case 'system_cleanup':
      return 'Ended automatically by Sidetone';
    case 'unknown':
      return 'We couldn’t determine who ended this call';
    default:
      break;
  }
  switch (t.termination_side) {
    case 'agent':
      return agentHeadline(actor);
    case 'remote':
      return 'The other side ended the call';
    case 'system':
      return 'Ended automatically by Sidetone';
    default:
      return 'We couldn’t determine who ended this call';
  }
}

function agentHeadline(actor: Actor): string {
  if (actor.kind === 'self') return 'You ended this call';
  if (actor.kind === 'teammate') return `Ended by ${actor.name ?? 'a teammate'} in Sidetone`;
  return 'Ended from your team’s side. We don’t know who ended it.';
}

/** Device-originated ends name the device, never a person's intent. */
function deviceHeadline(owner: Actor): string {
  if (owner.kind === 'self') return 'Your device ended the call';
  if (owner.kind === 'teammate') return `${owner.name ?? 'A teammate'}’s device ended the call`;
  return 'A device on your team’s side ended the call';
}

/**
 * A plain sentence for each termination kind (also used for events of that kind). `actor` names a
 * person only for explicit commands with a known actor; without one the sentence describes the side.
 */
export function terminationSentence(kind: TerminationKind, actor: Actor = { kind: 'unknown' }): string {
  switch (kind) {
    case 'agent_hangup_command':
      return actor.kind === 'unknown' ? 'The call was ended from your team’s side in Sidetone.' : `${subject(actor)} ended the call in Sidetone.`;
    case 'agent_device_bye':
      return actor.kind === 'unknown' ? 'A device on your team’s side hung up the call.' : `${possessive(actor)} device hung up the call.`;
    case 'agent_reject':
      return actor.kind === 'unknown' ? 'The call was rejected on your team’s side.' : `${subject(actor)} declined the call.`;
    case 'agent_cancel':
      return actor.kind === 'unknown' ? 'The call was cancelled on your team’s side before it was answered.' : `${subject(actor)} cancelled the call before it was answered.`;
    case 'remote_hangup':
      return 'The other side hung up.';
    case 'remote_busy':
      return 'The other side’s line was busy.';
    case 'remote_no_answer':
      return 'The other side didn’t answer in time.';
    case 'remote_rejected':
      return 'The call was rejected before it was answered.';
    case 'remote_cancel':
      return 'The other side hung up before the call was answered.';
    case 'carrier_failure':
      return 'The phone carrier couldn’t complete the call.';
    case 'registration_failure':
      return 'The device wasn’t connected to the phone system.';
    case 'signaling_lost':
      return 'The call control connection was lost.';
    case 'ice_failure':
      return 'No network path for the audio could be found.';
    case 'dtls_failure':
      return 'The encrypted audio connection couldn’t be set up.';
    case 'media_lost_auto_cleanup':
      return 'The app ended the call automatically after the audio connection was lost.';
    case 'duration_limit':
      return 'The call reached the workspace’s time limit.';
    case 'policy_stop':
      return 'A workspace policy stopped the call.';
    case 'system_cleanup':
      return 'Sidetone closed the call while cleaning up.';
    case 'unknown':
      return 'The call ended for a reason we couldn’t determine.';
  }
}

export const CONFIDENCE_LABEL: Record<Confidence, string> = {
  observed: 'Observed',
  inferred: 'Inferred',
  reported: 'Reported by the app',
  unknown: 'Unknown',
};

export const CONFIDENCE_HINT: Record<Confidence, string> = {
  observed: 'Sidetone’s server saw this happen.',
  inferred: 'Worked out from other events. Nobody saw it directly.',
  reported: 'A device sent this. The server didn’t see it directly.',
  unknown: 'There isn’t enough evidence to say.',
};

// ---- Timeline events ------------------------------------------------------------------------------

export const SOURCE_LABEL: Record<EventSource, string> = {
  agent_action: 'Agent action',
  client_report: 'Reported by the app',
  controller: 'Sidetone server',
  pbx: 'Phone system',
  carrier: 'Carrier',
  system: 'System',
};

const STATE_WORD: Record<string, string> = {
  new: 'not started',
  checking: 'checking',
  connecting: 'connecting',
  connected: 'connected',
  completed: 'connected',
  disconnected: 'interrupted',
  failed: 'failed',
  closed: 'closed',
  stable: 'stable',
};
export const stateWord = (state: string | undefined) => (state ? STATE_WORD[state] ?? 'changed' : null);

/**
 * Lifecycle and media event kinds. Provisional names until packages/contracts lists them; any
 * other kind falls back to a sentence for its category, and the code stays in the details element.
 */
const EVENT_SENTENCE: Record<string, (e: TimelineEvent, actor: Actor) => string> = {
  call_created: () => 'The call was created.',
  ringing: () => 'The call started ringing.',
  answered: () => 'The call was answered.',
  held: () => 'The call was put on hold.',
  resumed: () => 'The call was taken off hold.',
  hangup_requested: (_e, a) => `${subject(a)} asked to end the call.`,
  registered: () => 'The device connected to the phone system.',
  registration_lost: () => 'The device lost its connection to the phone system.',
  signaling_degraded: () => 'The call control connection dropped. Audio may have continued.',
  signaling_restored: () => 'The call control connection came back.',
  ice_state: e => `Network path for the audio: ${stateWord(e.data.ice_state) ?? 'changed'}.`,
  dtls_state: e => `Encrypted audio: ${stateWord(e.data.dtls_state) ?? 'changed'}.`,
  connection_state: e => `Audio connection: ${stateWord(e.data.connection_state) ?? 'changed'}.`,
  media_stats: () => 'Audio quality measurement.',
  media_lost: () => 'The audio connection was lost.',
  media_restored: () => 'The audio connection came back.',
  reconnecting: () => 'Trying to reconnect the audio.',
  reconnected: () => 'The audio reconnected.',
  // The call controller's duration safeguard found the line already gone (2026-10-29): the caller's own line for an inbound
  // call, or a line it was about to dial.
  duration_guard_skipped: () => 'The caller hung up before the call was set up.',
  leg_gone_before_dial: () => 'The line was gone before it could be dialed.',
  // Voicemail drop (B11, round 13): the agent's decision, the agent's line released, then how the message ended.
  voicemail_drop_started: (_e, a) => `${subject(a)} dropped a voicemail. The message started playing to the other side.`,
  voicemail_drop_agent_release: () => 'The agent’s line was released for the voicemail drop, so they could take the next call.',
  voicemail_drop_completed: () => 'The voicemail drop played to the end.',
  voicemail_drop_interrupted: () => 'The voicemail drop was interrupted before it finished.',
  voicemail_drop_failed: () => 'The voicemail drop couldn’t be played.',
  voicemail_drop_cleanup: () => 'Sidetone ended the call after the voicemail drop.',
};

const DROP_KIND = /^voicemail_drop_/;

/** A voicemail drop's row in the timeline (started, completed, interrupted and the rest). */
export function isDropEvent(e: Pick<TimelineEvent, 'kind'>): boolean {
  return DROP_KIND.test(e.kind);
}

/**
 * The line under a drop row, from the call's own record of the drop (the event carries only the fact): the message's
 * name when it started, and how much played when it ended. An interrupted drop's time is the controller's
 * elapsed-time estimate, and says so.
 */
export function dropEventDetail(e: Pick<TimelineEvent, 'kind'>, drop: { name?: unknown; played_ms?: unknown } | null | undefined): string | null {
  if (!drop || !isDropEvent(e)) return null;
  const name = typeof drop.name === 'string' && drop.name.trim() ? drop.name.replace(/[\u0000-\u001f\u007f]+/g, ' ').trim().slice(0, 100) : null;
  const played = typeof drop.played_ms === 'number' && Number.isFinite(drop.played_ms) && drop.played_ms >= 0 ? formatDuration(drop.played_ms) : null;
  switch (e.kind) {
    case 'voicemail_drop_started':
      return name ? `Message: ${name}` : null;
    case 'voicemail_drop_completed':
      return played ? `Played ${played}${name ? ` of ${name}` : ''}` : null;
    case 'voicemail_drop_interrupted':
      return played ? `Played about ${played}${name ? ` of ${name}` : ''} (estimated from elapsed time)` : null;
    default:
      return null;
  }
}

const CATEGORY_SENTENCE: Record<EventCategory, string> = {
  signaling: 'Call signaling event.',
  media: 'Audio connection event.',
  termination: 'The call ended.',
  registration: 'Phone connection event.',
  policy: 'Workspace policy event.',
  system: 'System event.',
};

// ---- Notes ------------------------------------------------------------------------------------------------

export type NoteAction = 'added' | 'edited' | 'deleted';
const NOTE_KIND = /^note(?:_(added|edited|deleted))?$/;
const NOTE_ID = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** Call notes reach the timeline as `note` events (B6): who added, edited or deleted a note. The text stays in the notes. */
export function isNoteEvent(e: Pick<TimelineEvent, 'kind'>): boolean {
  return NOTE_KIND.test(e.kind);
}

/** From the kind (`note_added`) or, for kind `note`, the cause code (`note_edited`). */
export function noteAction(e: Pick<TimelineEvent, 'kind' | 'cause_code'>): NoteAction | null {
  if (!isNoteEvent(e)) return null;
  const action = NOTE_KIND.exec(e.kind)?.[1] ?? NOTE_KIND.exec(e.cause_code ?? '')?.[1];
  return (action as NoteAction | undefined) ?? 'added';
}

/** The note's id, read from the raw detail before redaction (which would mask it as a long token). */
function noteIdFrom(value: unknown): string | null {
  return typeof value === 'string' ? NOTE_ID.exec(value)?.[1]?.toLowerCase() ?? null : null;
}

export function noteSentence(action: NoteAction, actor: Actor): string {
  return `${subject(actor)} ${action} a note.`;
}

export function eventSentence(e: TimelineEvent, actor: Actor = { kind: 'unknown' }): string {
  const note = noteAction(e);
  if (note) return noteSentence(note, actor);
  const kind = oneOf(TERMINATION_KINDS, e.kind);
  if (kind) return terminationSentence(kind, actor);
  const known = Object.hasOwn(EVENT_SENTENCE, e.kind) ? EVENT_SENTENCE[e.kind] : undefined;
  return known ? known(e, actor) : CATEGORY_SENTENCE[e.category];
}

/** Collapses long timelines: the first and last events stay visible around one "show more" gap. */
export const TIMELINE_HEAD = 25;
export const TIMELINE_TAIL = 25;

export function timelineWindow(count: number, expanded: boolean): { head: number; tailFrom: number; hidden: number } {
  if (expanded || count <= TIMELINE_HEAD + TIMELINE_TAIL + 5) return { head: count, tailFrom: count, hidden: 0 };
  return { head: TIMELINE_HEAD, tailFrom: count - TIMELINE_TAIL, hidden: count - TIMELINE_HEAD - TIMELINE_TAIL };
}

/** Roving focus in the event list: arrows move one, Home and End jump. Null for other keys. */
export function moveFocus(key: string, index: number, count: number): number | null {
  if (count <= 0) return null;
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      return Math.min(index + 1, count - 1);
    case 'ArrowUp':
    case 'ArrowLeft':
      return Math.max(index - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

export function clockTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(date);
}

// ---- Media health per leg -------------------------------------------------------------------------

export type HealthProvenance = 'app' | 'server';

export const HEALTH_LABEL: Record<HealthProvenance, string> = { app: 'Reported by the app', server: 'Observed by the server' };

export interface LegHealth {
  key: string;
  legId: string | null;
  participantId: string | null;
  provenance: HealthProvenance;
  samples: number;
  /** From the latest sample with both counters. */
  lossPercent: number | null;
  /** Worst value seen. */
  jitterMs: number | null;
  rttMs: number | null;
  reconnects: number | null;
  iceState: string | null;
  dtlsState: string | null;
  connectionState: string | null;
  lastAt: string;
}

const HAS_HEALTH = ['packets_received', 'packets_lost', 'jitter_ms', 'rtt_ms', 'reconnects', 'ice_state', 'dtls_state', 'connection_state'] as const;

/** Media health from client reports ("reported by the app") and PBX events ("observed by the server"). */
export function mediaHealth(events: readonly TimelineEvent[]): LegHealth[] {
  const legs = new Map<string, LegHealth>();
  for (const e of events) {
    const provenance: HealthProvenance | null = e.source === 'client_report' ? 'app' : e.source === 'pbx' ? 'server' : null;
    if (!provenance || !HAS_HEALTH.some(k => e.data[k] !== undefined)) continue;
    const key = `${provenance}:${e.leg_id ?? e.participant_id ?? 'call'}`;
    let leg = legs.get(key);
    if (!leg) {
      leg = { key, legId: e.leg_id, participantId: e.participant_id, provenance, samples: 0, lossPercent: null, jitterMs: null, rttMs: null, reconnects: null, iceState: null, dtlsState: null, connectionState: null, lastAt: e.at };
      legs.set(key, leg);
    }
    const d = e.data;
    leg.samples += 1;
    leg.lastAt = e.at;
    if (d.packets_received !== undefined && d.packets_lost !== undefined) {
      const total = d.packets_received + d.packets_lost;
      leg.lossPercent = total > 0 ? (d.packets_lost / total) * 100 : 0;
    }
    if (d.jitter_ms !== undefined) leg.jitterMs = Math.max(leg.jitterMs ?? 0, d.jitter_ms);
    if (d.rtt_ms !== undefined) leg.rttMs = Math.max(leg.rttMs ?? 0, d.rtt_ms);
    if (d.reconnects !== undefined) leg.reconnects = Math.max(leg.reconnects ?? 0, d.reconnects);
    if (d.ice_state) leg.iceState = d.ice_state;
    if (d.dtls_state) leg.dtlsState = d.dtls_state;
    if (d.connection_state) leg.connectionState = d.connection_state;
  }
  return [...legs.values()];
}

export function formatLoss(percent: number): string {
  if (percent === 0) return '0%';
  if (percent < 0.1) return 'under 0.1%';
  return `${percent < 10 ? percent.toFixed(1) : Math.round(percent)}%`;
}

export const formatMs = (ms: number) => `${Math.round(ms)} ms`;

// ---- Calls list label -------------------------------------------------------------------------------

export type TagIcon = 'agent' | 'remote' | 'network' | 'carrier' | 'media' | 'limit' | 'system' | 'unknown';

export interface TerminationTag {
  text: string;
  icon: TagIcon;
  confidence: Confidence | null;
}

const TAG: Record<TerminationKind, [string, TagIcon]> = {
  agent_hangup_command: ['Ended by your team', 'agent'],
  agent_device_bye: ['Ended by a team device', 'agent'],
  agent_reject: ['Declined by your team', 'agent'],
  agent_cancel: ['Cancelled by your team', 'agent'],
  remote_hangup: ['Other side ended', 'remote'],
  remote_busy: ['Other side busy', 'remote'],
  remote_no_answer: ['No answer', 'remote'],
  remote_rejected: ['Rejected', 'remote'],
  remote_cancel: ['Other side hung up', 'remote'],
  carrier_failure: ['Carrier problem', 'carrier'],
  registration_failure: ['Phone not connected', 'network'],
  signaling_lost: ['Connection lost', 'network'],
  ice_failure: ['Audio couldn’t connect', 'network'],
  dtls_failure: ['Audio couldn’t connect', 'network'],
  media_lost_auto_cleanup: ['Ended after audio loss', 'media'],
  duration_limit: ['Time limit', 'limit'],
  policy_stop: ['Stopped by policy', 'limit'],
  system_cleanup: ['Ended by Sidetone', 'system'],
  unknown: ['Ended, cause unknown', 'unknown'],
};

/** A compact label for the calls list. Null when the server hasn't attributed the end: nothing is invented. */
export function terminationTag(t: CallTermination): TerminationTag | null {
  if (t.termination_kind) {
    const [text, icon] = TAG[t.termination_kind];
    return { text, icon, confidence: t.termination_confidence };
  }
  switch (t.termination_side) {
    case 'agent':
      return { text: 'Ended by your team', icon: 'agent', confidence: t.termination_confidence };
    case 'remote':
      return { text: 'Other side ended', icon: 'remote', confidence: t.termination_confidence };
    case 'system':
      return { text: 'Ended by Sidetone', icon: 'system', confidence: t.termination_confidence };
    case 'unknown':
      return { text: 'Ended, cause unknown', icon: 'unknown', confidence: t.termination_confidence };
    default:
      return null;
  }
}

export function callEnded(call: Pick<Call, 'state'>): boolean {
  return call.state === 'ended' || call.state === 'failed';
}

// ---- View state ---------------------------------------------------------------------------------------

export type TimelineView =
  | { state: 'denied'; code: string | null; requestId: string | null }
  | { state: 'not_found'; requestId: string | null }
  | { state: 'loading' }
  | { state: 'access'; access: 'refreshing' | 'blocked' }
  | { state: 'error'; error: { title: string; detail: string; code: string; requestId: string | null; retryable: boolean } }
  | { state: 'empty' }
  | { state: 'ready'; timeline: CallTimeline };

export interface TimelineInput {
  canRead: boolean;
  status: 'idle' | 'loading' | 'ready' | 'error' | 'refreshing' | 'blocked';
  value: { data?: unknown } | null;
  error: { title: string; detail: string; code: string; requestId: string | null; retryable: boolean; status?: number } | null;
}

/** Decides exactly one state for the panel: denied, not found, loading, access check, error, empty or ready. */
export function timelineView(input: TimelineInput): TimelineView {
  if (!input.canRead) return { state: 'denied', code: null, requestId: null };
  if (input.status === 'refreshing' || input.status === 'blocked') return { state: 'access', access: input.status };
  if (input.status === 'error' && input.error) {
    if (input.error.status === 403 && input.error.code !== 'mfa_required') return { state: 'denied', code: input.error.code, requestId: input.error.requestId };
    if (input.error.status === 404) return { state: 'not_found', requestId: input.error.requestId };
    return { state: 'error', error: input.error };
  }
  if (input.status !== 'ready' || !input.value) return { state: 'loading' };
  const timeline = readTimeline(input.value.data);
  if (!timeline) return { state: 'error', error: { title: 'The server sent diagnostics this version can’t read', detail: '', code: 'invalid_response', requestId: null, retryable: true } };
  if (timeline.events.length === 0 && timeline.skipped === 0 && !hasAttribution(timeline.call)) return { state: 'empty' };
  return { state: 'ready', timeline };
}
